import { Edit } from '@mui/icons-material';
import {
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getDocumentsAvailableForConfirming } from 'lib/api/confirming';
import { DocumentsAvailableForConfirming } from 'lib/api/confirming/types';
import { DocumentOperationOrigin } from 'lib/api/document/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import EditDocumentManually from '../../../../../common/documentsSelector/components/editDocument';
import { DocumentWithMetadata } from '../../../../../common/documentsSelector/types';
import OperationResume from '../OperationResume';
import AddDocumentsConfirming from './components/AddDocumentsConfirming';

const DocumentSelector = (props: any) => {
  const {
    business,
    selected,
    setSelected,
    documents,
    setDocuments,
    setCachedDocuments,
    cachedDocuments,
    setSelectedDocuments,
  } = props;

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [businessName, setBusinessName] = useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [folio, setFolio] = useState<string>('');
  const [enrolledType, setEnrolledType] = useState<string>('manual');
  const [configuration, setConfiguration] = useState<any>({});
  const [tableLoading, setTableLoading] = useState(false);
  const [openEditDocument, setOpenEditDocument] = useState<boolean>(false);
  const [documentToEdit, setDocumentToEdit] =
    useState<DocumentsAvailableForConfirming | null>(null);
  const [openAddDocumentsConfirming, setOpenAddDocumentsConfirming] =
    useState(false);

  const isEnrolled = false;

  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const generateQuery = ({
    page,
    pageSize,
    overrideEnrolledType,
  }: {
    page: number;
    pageSize: number;
    overrideEnrolledType?: string;
  }) => {
    let query = '';
    if (folio) query += `folio=${folio}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName)
      query += `businessName=${businessName.replace('&', '%26')}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (selected.length) query += `selectedDocuments=${selected.join(',')}&`;
    if (overrideEnrolledType) {
      query += `enrolledType=${overrideEnrolledType}&`;
    } else if (enrolledType) query += `enrolledType=${enrolledType}&`;
    return query;
  };

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const getDocumentsAvailable = async ({
    page,
    pageSize,
    forceEnrolledType,
  }: {
    page: number;
    pageSize: number;
    forceEnrolledType?: string;
  }) => {
    setTableLoading(true);
    if (forceEnrolledType) {
      setEnrolledType(forceEnrolledType);
    }
    const query = generateQuery({
      page,
      pageSize,
      overrideEnrolledType: forceEnrolledType,
    });
    try {
      const response = await getDocumentsAvailableForConfirming(
        dispatch,
        {
          identifier: business.identifier,
          subProductTypeId: 'electronic-invoice-confirming',
        },
        query,
      );
      if (!response) {
        throw new Error('Error al obtener documentos disponibles');
      }
      setDocuments(response.data);
      setCachedDocuments([
        ...cachedDocuments,
        ...response.data,
        ...response.selectedDocuments,
      ]);
      setPagination(response.pagination);
      setConfiguration(response.configuration);
      setTableLoading(false);
      return response;
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al obtener documentos disponibles');
      setTableLoading(false);
    }
  };

  const triggerSearch = async () => {
    setLoading(true);
    await getDocumentsAvailable({
      page: 0,
      pageSize: PAGE_SIZE,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (business?.identifier) {
      void triggerSearch();
    }
  }, []);

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de confirmación',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre proveedor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut proveedor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];

  const handleSetSelected = (selectedIds: any) => {
    const newCachedDocuments = [...cachedDocuments];
    for (const id of selectedIds) {
      const document = documents.find((document: any) => document.id === id);
      if (document) {
        const newCachedIds = [
          ...newCachedDocuments.map((document: any) => document.id),
        ];
        if (!newCachedIds.includes(document.id)) {
          newCachedDocuments.push(document);
        }
      }
    }
    setCachedDocuments(newCachedDocuments);
    setSelected(selectedIds);
  };

  const cantSelectDocuments = (document: any) => {
    if (!configuration) {
      return false;
    }
    if (configuration.showOnlyAvailablesNotInRequest) {
      if (document.requestId) {
        return true;
      }
    }
    if (configuration.dontFilterOwnInvoices) {
      if (document.cessionaryIdentifier === configuration.companyIdentifier) {
        return false;
      }
    }
    if (configuration.showOnlyAvailables) {
      if (
        document.status === 'Transable' ||
        enrolledType === 'manual' ||
        !isEnrolled
      ) {
        return false;
      }
    }
    if (
      !configuration.dontFilterOwnInvoices &&
      !configuration.showOnlyAvailables
    ) {
      return false;
    }
    return true;
  };

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }

  return (
    <div>
      <OperationResume
        stakeholderName={business.businessName}
        stakeholderIdentifier={business.identifier}
      />
      <Divider />
      <div style={{ padding: '20px 0px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <ToggleButtonGroup
              value={enrolledType || 'manual'}
              color="primary"
              exclusive
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setEnrolledType(newValue);
                  void (async () => {
                    await getDocumentsAvailable({
                      page: 1,
                      pageSize: PAGE_SIZE,
                      forceEnrolledType: newValue,
                    });
                  })();
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: '140px',
                },
              }}
            >
              <ToggleButton value="sii">Desde SII</ToggleButton>
              <ToggleButton value="manual">Creados manualmente</ToggleButton>
            </ToggleButtonGroup>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <GlobalSearcher
              filters={filters}
              triggerSearch={triggerSearch}
              dispatch={dispatch}
              disableSearch={loading || tableLoading}
            />
            <CustomButton
              variant="contained"
              color="primary"
              onClick={() => setOpenAddDocumentsConfirming(true)}
            >
              Agregar documentos
            </CustomButton>
          </div>
        </div>
        <div>
          <Typography>{selected.length} documentos seleccionados.</Typography>
        </div>
        <div>
          <TableWithPagination
            dataHeaders={[
              { key: 'folio', label: 'Folio' },
              { key: 'amount', label: 'Monto', type: 'money' },
              {
                key: 'amountAvailable',
                label: 'Monto disponible',
                type: 'money',
              },
              {
                key: 'businessName',
                label: 'Proveedor',
              },
              {
                key: 'issuedDate',
                label: 'Fecha de emisión',
                type: 'date',
              },
              {
                key: 'receiptDate',
                label: 'Fecha de recepción',
                type: 'eight-days-date',
              },
              {
                key: 'confirmDate',
                label: 'Fecha de confirmación',
                type: 'date',
              },
              {
                key: 'requestId',
                label: 'Solicitud',
              },
              {
                key: 'status',
                label: 'Estado',
                type: 'cession',
              },
              {
                key: 'cessionary',
                label: 'Cessionario',
              },
              ...(enrolledType === 'manual'
                ? [
                    {
                      key: 'edit',
                      label: 'Editar',
                      type: 'custom',
                      customComponent: (_value: unknown, row: any) => {
                        return (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDocumentToEdit(row);
                              setOpenEditDocument(true);
                            }}
                            sx={{ padding: '4px' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        );
                      },
                    },
                  ]
                : []),
            ]}
            cantSelectRow={cantSelectDocuments}
            data={documents}
            selected={selected}
            setSelected={handleSetSelected}
            setData={setDocuments}
            pagination={pagination}
            setPagination={setPagination}
            loading={tableLoading}
            searchData={getDocumentsAvailable}
          />
        </div>
      </div>
      {openAddDocumentsConfirming && (
        <AddDocumentsConfirming
          open={openAddDocumentsConfirming}
          setOpen={setOpenAddDocumentsConfirming}
          stakeholderIdentifier={business.identifier}
          handleSearchSuggestedDocuments={() => void triggerSearch()}
        />
      )}
      {openEditDocument && documentToEdit && (
        <EditDocumentManually
          business={{
            id: business.identifier,
            identifier: business.identifier,
            businessName: business.businessName,
          }}
          open={openEditDocument}
          setOpen={setOpenEditDocument}
          document={documentToEdit as DocumentWithMetadata | null}
          onDocumentUpdated={(updatedDocument) => {
            setDocuments((prev: DocumentsAvailableForConfirming[]) =>
              prev.map((doc: DocumentsAvailableForConfirming) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            setSelectedDocuments((prev: DocumentWithMetadata[]) =>
              prev.map((doc: DocumentWithMetadata) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            setCachedDocuments((prev: DocumentsAvailableForConfirming[]) =>
              prev.map((doc: DocumentsAvailableForConfirming) =>
                doc.id === updatedDocument.id ? updatedDocument : doc,
              ),
            );
            setOpenEditDocument(false);
            setDocumentToEdit(null);
          }}
          origin={DocumentOperationOrigin.CONFIRMING}
        />
      )}
    </div>
  );
};

export default DocumentSelector;
